import { Loader2, MessageSquare, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";

type SessionEntry = {
  key: string;
  label?: string;
  sessionId?: string;
  updatedAt?: number;
  channel?: string;
  model?: string;
};

function parseContextType(key: string): string {
  if (key.includes(":lx:global")) { return "global"; }
  if (key.includes(":lx:research:")) { return "research"; }
  if (key.includes(":lx:case:") && key.includes(":proc:")) { return "processo"; }
  if (key.includes(":lx:case:")) { return "case"; }
  return "other";
}

const CTX_COLORS: Record<string, string> = {
  global: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  case: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  processo: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  research: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  other: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

export function SessionsBrowser() {
  const { rpc, state } = useGatewayRpc();
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [compacting, setCompacting] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (state !== "connected") { return; }
    try {
      setLoading(true);
      const res = await rpc<{ entries: SessionEntry[] }>("sessions.list", {});
      setSessions(res.entries ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, [rpc, state]);

  useEffect(() => { load(); }, [load]);

  const loadPreview = async (key: string) => {
    setSelected(key);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await rpc<{ messages?: Array<{ role: string; text: string }> }>("sessions.preview", { key });
      const msgs = res.messages ?? [];
      setPreview(msgs.map((m) => `[${m.role}] ${m.text}`).join("\n\n"));
    } catch {
      setPreview("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCompact = async (key: string) => {
    if (!confirm(`Compact session ${key.slice(0, 40)}...?`)) { return; }
    setCompacting(key);
    try {
      await rpc("sessions.compact", { key });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Compact failed");
    } finally {
      setCompacting(null);
    }
  };

  if (state !== "connected") {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Sessions</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Sessions ({sessions.length})</h1>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">{error}</div>
      )}

      <div className="grid h-[calc(100vh-12rem)] grid-cols-[1fr_1fr] gap-4">
        {/* Session list */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <MessageSquare className="h-4 w-4" /> Sessions
            </CardTitle>
          </CardHeader>
          <CardContent className="h-full overflow-y-auto p-0">
            {loading ? (
              <p className="p-4 text-center text-sm text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading...</p>
            ) : sessions.length === 0 ? (
              <p className="p-4 text-center text-sm text-slate-400">No sessions found</p>
            ) : (
              <div className="space-y-0.5 p-2">
                {sessions.map((s) => {
                  const ctx = parseContextType(s.key);
                  return (
                    <button key={s.key} type="button" onClick={() => loadPreview(s.key)}
                      className={`flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
                        selected === s.key ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                          : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"
                      }`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-medium ${CTX_COLORS[ctx]}`}>{ctx}</span>
                          <span className="truncate font-medium text-xs">{s.label || s.key.slice(0, 35)}</span>
                        </div>
                        <p className="mt-0.5 truncate text-[10px] text-muted-foreground font-mono">{s.key}</p>
                        {s.updatedAt && <p className="text-[10px] text-muted-foreground">{new Date(s.updatedAt).toLocaleString()}</p>}
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleCompact(s.key); }}
                        disabled={compacting === s.key} title="Compact"
                        className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                        {compacting === s.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      </button>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="flex flex-col overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {selected ? "Transcript Preview" : "Select a session"}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0">
            {previewLoading ? (
              <p className="p-4 text-center text-sm text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading...</p>
            ) : preview ? (
              <pre className="whitespace-pre-wrap p-4 font-mono text-xs leading-relaxed text-slate-300">{preview}</pre>
            ) : (
              <p className="p-4 text-center text-sm text-slate-400">Click a session to preview transcript</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
